import { useEffect, useMemo, useRef, useState } from "react";
import { gsap } from "gsap";
import { useTranslation } from "react-i18next";

// ids are stable DOM anchors, not translated content.
const SECTION_IDS = [
  { id: "hero", key: "hero" },
  { id: "stats", key: "stats" },
  { id: "core-values", key: "coreValues" },
  { id: "security", key: "security" },
  { id: "workflow", key: "workflow" },
];

export default function ScrollNav() {
  const [active, setActive] = useState(0);
  const [hovered, setHovered] = useState(null);
  const [visible, setVisible] = useState(false);
  const dotRefs = useRef([]);
  const lineRefs = useRef([]);
  const { t } = useTranslation();
  const nav = useMemo(() => {
    const items = t("scrollNav.items", { returnObjects: true });
    return SECTION_IDS.map(({ id, key }) => ({ id, label: items[key] }));
  }, [t]);

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      // Hide on very top
      setVisible(window.scrollY > 100);

      const scrollMid = window.scrollY + window.innerHeight / 2;

      nav.forEach((sec, i) => {
        const el = document.getElementById(sec.id);
        if (!el) return;
        const top = el.offsetTop;
        const bottom = top + el.offsetHeight;
        if (scrollMid >= top && scrollMid < bottom) {
          setActive(i);
        }
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [nav]);

  // Animate line fill between dots
  useEffect(() => {
    lineRefs.current.forEach((line, i) => {
      if (!line) return;
      gsap.to(line, {
        scaleY: i < active ? 1 : 0,
        duration: 0.5,
        ease: "power2.inOut",
        transformOrigin: "top center",
      });
    });
  }, [active]);

  // Animate dot on active change
  useEffect(() => {
    dotRefs.current.forEach((dot, i) => {
      if (!dot) return;
      gsap.to(dot, {
        scale: i === active ? 1.4 : 1,
        duration: 0.4,
        ease: "back.out(2)",
      });
    });
  }, [active]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav
      className={`scroll-nav ${visible ? "scroll-nav--visible" : ""}`}
      aria-label="Section navigation"
    >
      {nav.map((sec, i) => (
        <div key={sec.id} className="scroll-nav__item">

          {/* Connector line between dots */}
          {i > 0 && (
            <div className="scroll-nav__line-wrap">
              {/* Track (background) */}
              <div className="scroll-nav__line-track" />
              {/* Fill (animated) */}
              <div
                ref={(el) => (lineRefs.current[i - 1] = el)}
                className="scroll-nav__line-fill"
                style={{ transform: "scaleY(0)" }}
              />
            </div>
          )}

          {/* Dot + Label row */}
          <div className="scroll-nav__dot-row">

            {/* Label — slides in from right on hover or active */}
            <div
              className={`scroll-nav__label ${
                hovered === i || active === i ? "scroll-nav__label--visible" : ""
              }`}
            >
              {sec.label}
            </div>

            {/* Dot */}
            <button
              ref={(el) => (dotRefs.current[i] = el)}
              className={`scroll-nav__dot ${
                active === i ? "scroll-nav__dot--active" : ""
              }`}
              onClick={() => scrollTo(sec.id)}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              aria-label={`Go to ${sec.label}`}
            >
              {/* Inner glow ring */}
              {active === i && (
                <span className="scroll-nav__glow" />
              )}
            </button>

          </div>
        </div>
      ))}
    </nav>
  );
}